import { GetCommand, PutCommand, QueryCommand, UpdateCommand, DeleteCommand } from '@aws-sdk/lib-dynamodb';
import { config } from './config';
import { getDdb } from './aws';
import { Upload, UploadStatus } from './types';

/**
 * Fetches the caller's complete upload list in one call (looping the underlying Query via
 * LastEvaluatedKey). Sort, search, and grid/list view all need the full set in memory to behave
 * correctly, and at this project's catalog scale (hundreds, low thousands) that's cheap — see
 * design.md's "full-catalog fetch" decision in the improve-library-ux change.
 */
export async function listMyUploads(userId: string): Promise<Upload[]> {
  const items: Upload[] = [];
  let exclusiveStartKey: Record<string, unknown> | undefined;
  do {
    const r = await getDdb().send(
      new QueryCommand({
        TableName: config.uploadsTable,
        KeyConditionExpression: 'userId = :u',
        ExpressionAttributeValues: { ':u': userId },
        ScanIndexForward: false,
        Limit: 500,
        ExclusiveStartKey: exclusiveStartKey,
      }),
    );
    items.push(...((r.Items ?? []) as Upload[]));
    exclusiveStartKey = r.LastEvaluatedKey as Record<string, unknown> | undefined;
  } while (exclusiveStartKey);
  return items;
}

export async function getMyUpload(userId: string, uploadId: string): Promise<Upload | null> {
  const r = await getDdb().send(
    new GetCommand({ TableName: config.uploadsTable, Key: { userId, uploadId } }),
  );
  return (r.Item as Upload | undefined) ?? null;
}

export async function createUploadRow(row: Upload): Promise<void> {
  await getDdb().send(new PutCommand({ TableName: config.uploadsTable, Item: row }));
}

export async function setUploadStatus(
  userId: string,
  uploadId: string,
  status: UploadStatus,
): Promise<void> {
  await getDdb().send(
    new UpdateCommand({
      TableName: config.uploadsTable,
      Key: { userId, uploadId },
      UpdateExpression: 'SET #status = :s, #updatedAt = :t',
      ExpressionAttributeNames: { '#status': 'status', '#updatedAt': 'updatedAt' },
      ExpressionAttributeValues: { ':s': status, ':t': new Date().toISOString() },
    }),
  );
}

export async function deleteUploadRow(userId: string, uploadId: string): Promise<void> {
  await getDdb().send(
    new DeleteCommand({ TableName: config.uploadsTable, Key: { userId, uploadId } }),
  );
}

export function buildSourceKey(identityId: string, uploadId: string, filename: string): string {
  return `users/${identityId}/${uploadId}/${filename}`;
}

/**
 * A fresh, unique key on every call (rather than a fixed `thumbnail.jpg`) so a newly-picked
 * thumbnail is never masked by a stale CloudFront cache entry for the previous one at the same
 * URL — no invalidation needed, the old key's cached response just goes unused.
 */
export function buildThumbnailKey(identityId: string, uploadId: string): string {
  return `thumbnails/users/${identityId}/${uploadId}/thumbnail-${Date.now()}.jpg`;
}

/** Same fresh-key-per-call reasoning as buildThumbnailKey — a newly-uploaded caption file must
 * never be masked by a stale CloudFront cache entry for a previous one at the same URL. */
export function buildCaptionKey(identityId: string, uploadId: string): string {
  return `captions/users/${identityId}/${uploadId}/caption-${Date.now()}.vtt`;
}
